"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.PHASE_WARNING = exports.PHASE_LOCKED = exports.PHASE_ACTIVE = exports.IDLE_DEFAULTS = exports.END_REASON_SIGNOUT = exports.END_REASON_IDLE = void 0;
exports.computePhase = computePhase;
exports.createIdleClock = createIdleClock;
exports.default = void 0;
exports.parseActivityStamp = parseActivityStamp;
exports.parseSessionEnd = parseSessionEnd;
exports.readCookieValue = readCookieValue;
/**
 * The shared idle clock.
 *
 * Signs a person out after a set time with no activity, across every tab they
 * have open, on every SamSurveyor subdomain.
 *
 * ── Why a cookie ──
 *
 * Idleness belongs to the person, not to the tab. Somebody working in Designer
 * for an hour must not be signed out of Console. So every tab has to know when
 * any tab was last used.
 *
 * localStorage and BroadcastChannel are scoped to one origin, and
 * console.samsurveyor.com and designer.samsurveyor.com are different origins.
 * Neither crosses that line. A cookie scoped to the parent domain does, and it
 * is readable from JavaScript. So a cookie is the shared clock.
 *
 * Two cookies:
 *   <prefix>_last_activity   the time of the last human action, epoch ms
 *   <prefix>_session_end     "<epoch ms>:<reason>" once the session has ended
 *
 * Neither holds a secret and neither names anybody.
 *
 * The second cookie is what makes the Logout button empty every tab. One tab
 * writes it, and the others see it on their next read.
 *
 * ── Why timestamps and not a countdown ──
 *
 * A laptop that sleeps stops JavaScript timers. A countdown would resume where
 * it left off and think no time had passed, so a machine asleep for an hour
 * would come back still signed in. Comparing the current time against the
 * cookie is correct across sleep, because the wall clock kept running.
 *
 * ── Nothing here talks to a network ──
 *
 * This module decides *when*. It never signs anybody out itself. It reports a
 * phase, and the app decides what to do about it. That keeps it testable and
 * keeps the lock working when the network is down.
 *
 * ── The context label is not part of the cross-tab protocol ──
 *
 * setContextLabel names what a tab has open — a survey title, a channel being
 * configured — so its own lock screen can say what it was, not just that it
 * happened. This is plain in-memory state, one tab's own, never a cookie.
 * Nothing carries it to another tab, and nothing needs to: a tab only ever
 * shows its own lock screen, never another tab's.
 */

/** Phases, in order. A session only ever moves forwards through these. */
const PHASE_ACTIVE = exports.PHASE_ACTIVE = 'active';
const PHASE_WARNING = exports.PHASE_WARNING = 'warning';
const PHASE_LOCKED = exports.PHASE_LOCKED = 'locked';
const IDLE_DEFAULTS = exports.IDLE_DEFAULTS = {
  /** Warn at 28 minutes, so there are two minutes left to save and react. */
  warnAfterMs: 28 * 60 * 1000,
  /** Lock at 30 minutes. */
  lockAfterMs: 30 * 60 * 1000,
  /** Look at the clock this often. Small enough that tabs lock together. */
  tickMs: 5 * 1000,
  /** Write the activity cookie at most this often, not on every keystroke. */
  writeThrottleMs: 30 * 1000,
  /** How long the lock waits for an unfinished draft save before giving up. */
  saveGraceMs: 10 * 1000,
  /**
   * How often to tell the server that a person is here.
   *
   * This is what makes the 30-minute rule enforceable on the server. The
   * cookie only works while a browser is running; close a laptop and nothing
   * revokes anything.
   *
   * A minute against a 30-minute window is plenty. Several tabs share one
   * user record and each of them sends, and the server throttles the write
   * again on its side, so a shorter interval would only add traffic.
   */
  heartbeatMs: 60 * 1000
};

/** Deliberate actions only. Bare mouse movement is not on this list, because a
 * cat on a desk or a USB mouse jiggler would hold a session open all night. */
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'];

/** Why a session ended. Shown to the person, so keep them distinguishable. */
const END_REASON_IDLE = exports.END_REASON_IDLE = 'idle';
const END_REASON_SIGNOUT = exports.END_REASON_SIGNOUT = 'signout';

/**
 * Read one cookie by name out of a raw cookie string.
 * Returns '' when it is absent, which is the same answer as empty.
 */
function readCookieValue(rawCookie, name) {
  const parts = String(rawCookie || '').split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const eq = trimmed.indexOf('=');
    if (eq < 0) continue;
    if (trimmed.slice(0, eq) !== name) continue;
    try {
      return decodeURIComponent(trimmed.slice(eq + 1));
    } catch (err) {
      return trimmed.slice(eq + 1);
    }
  }
  return '';
}

/**
 * Turn a stored activity value into a time, or null when it cannot be trusted.
 *
 * Anything that is not a positive whole number is treated as absent rather than
 * as zero. Zero would read as 1970, which would lock every tab instantly.
 */
function parseActivityStamp(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  if (!/^\d+$/.test(raw)) return null;
  const value = Number(raw);
  if (!Number.isSafeInteger(value) || value <= 0) return null;
  return value;
}

/** Split a session-end cookie into its time and its reason. */
function parseSessionEnd(raw) {
  if (typeof raw !== 'string' || raw === '') return null;
  const separator = raw.indexOf(':');
  const stampText = separator < 0 ? raw : raw.slice(0, separator);
  const reason = separator < 0 ? END_REASON_IDLE : raw.slice(separator + 1);
  const at = parseActivityStamp(stampText);
  if (at === null) return null;
  return {
    at,
    reason: reason || END_REASON_IDLE
  };
}

/**
 * Work out the phase from the raw facts. No side effects, no clock, no cookies.
 * Everything time-dependent is an argument, so this is trivial to test.
 */
function computePhase({
  now,
  lastActivityAt,
  sessionEnd,
  warnAfterMs,
  lockAfterMs
}) {
  if (sessionEnd) {
    return {
      phase: PHASE_LOCKED,
      reason: sessionEnd.reason,
      idleMs: 0,
      msUntilLock: 0
    };
  }
  if (lastActivityAt === null || lastActivityAt === undefined) {
    // No cookie yet. A tab that has only just opened is not idle.
    return {
      phase: PHASE_ACTIVE,
      reason: '',
      idleMs: 0,
      msUntilLock: lockAfterMs
    };
  }
  // A stamp in the future would otherwise give a negative idle time and never
  // lock. Treat it as "just now".
  const idleMs = Math.max(0, now - lastActivityAt);
  const msUntilLock = Math.max(0, lockAfterMs - idleMs);
  if (idleMs >= lockAfterMs) {
    return {
      phase: PHASE_LOCKED,
      reason: END_REASON_IDLE,
      idleMs,
      msUntilLock: 0
    };
  }
  if (idleMs >= warnAfterMs) {
    return {
      phase: PHASE_WARNING,
      reason: '',
      idleMs,
      msUntilLock
    };
  }
  return {
    phase: PHASE_ACTIVE,
    reason: '',
    idleMs,
    msUntilLock
  };
}

/** The default cookie jar: the real document. */
function browserCookieJar(cookieDomain) {
  return {
    readAll() {
      return typeof document !== 'undefined' && document.cookie || '';
    },
    write(name, value) {
      if (typeof document === 'undefined') return;
      const bits = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax'];
      // Only ask for a domain we are actually on. A browser refuses a
      // cookie for another domain, and it refuses silently.
      if (cookieDomain) bits.push(`Domain=${cookieDomain}`);
      // Secure on http would be refused, and local development is http.
      if (typeof window !== 'undefined' && window.location && window.location.protocol === 'https:') {
        bits.push('Secure');
      }
      // No Expires, so the cookie dies with the browser.
      document.cookie = bits.join('; ');
    },
    remove(name) {
      if (typeof document === 'undefined') return;
      const bits = [`${name}=`, 'Path=/', 'Max-Age=0', 'SameSite=Lax'];
      if (cookieDomain) bits.push(`Domain=${cookieDomain}`);
      document.cookie = bits.join('; ');
    }
  };
}

/**
 * Build an idle clock.
 *
 * Every moving part is injectable so the tests never need a browser or a real
 * clock.
 *
 * @param {object}   options
 * @param {string}   [options.cookiePrefix]  names both cookies. Default 'sam'.
 * @param {string}   [options.cookieDomain]  parent domain, or null/undefined
 *                                           for a cookie scoped to this host.
 *                                           SuperPanel uses the host-scoped
 *                                           form, because its session is its
 *                                           own and must stay separate.
 * @param {function} [options.now]           returns the current epoch ms.
 * @param {object}   [options.cookies]       cookie jar; see browserCookieJar.
 * @param {number}   [options.warnAfterMs]
 * @param {number}   [options.lockAfterMs]
 * @param {number}   [options.tickMs]
 * @param {number}   [options.writeThrottleMs]
 * @param {number}   [options.saveGraceMs]
 */
function createIdleClock(options = {}) {
  const {
    cookiePrefix = 'sam',
    cookieDomain = null,
    now = () => Date.now(),
    cookies = browserCookieJar(cookieDomain),
    warnAfterMs = IDLE_DEFAULTS.warnAfterMs,
    lockAfterMs = IDLE_DEFAULTS.lockAfterMs,
    tickMs = IDLE_DEFAULTS.tickMs,
    writeThrottleMs = IDLE_DEFAULTS.writeThrottleMs,
    saveGraceMs = IDLE_DEFAULTS.saveGraceMs,
    heartbeatMs = IDLE_DEFAULTS.heartbeatMs,
    // Called when a person has done something and it is time to tell the
    // server. Optional: with no handler the clock is browser-only, which is
    // the right shape for a page with no session to report about.
    onHeartbeat = null
  } = options;
  const ACTIVITY_COOKIE = `${cookiePrefix}_last_activity`;
  const END_COOKIE = `${cookiePrefix}_session_end`;
  const subscribers = new Set();
  /** Draft savers, registered by any panel holding unsaved work. */
  const draftSavers = new Map();
  let nextSaverId = 1;
  let running = false;
  let timer = null;
  let lastWriteAt = 0;
  let lastPhase = PHASE_ACTIVE;
  /** The promise for the current run of the draft savers, or null. */
  let savePromise = null;
  /** True once this idle stretch has already had its drafts saved. */
  let draftsSavedThisStretch = false;
  /** True once this tab has decided it is the one to perform the sign-out. */
  let claimedSignOut = false;
  /** What the savers reported last time they ran. */
  let lastSaveResults = [];
  /** When the server was last told a person is here. */
  let lastHeartbeatAt = -Infinity;
  /**
   * The end-cookie value this tab found when it started, and therefore the
   * one broadcast it did not hear and must not act on. See readSessionEnd.
   */
  let ignoredEndRaw = null;
  /**
   * A plain-language name for whatever the person has open right now — a
   * survey title, a channel being configured, whatever the app can name.
   *
   * The app keeps this current while it runs. Once the tab unmounts at the
   * lock, nothing updates it again, so the lock screen shows the last value
   * it held — a description of the work, not the work itself. It is never
   * sent anywhere and never touches a cookie; it only has to survive the
   * unmount, and a closure does that.
   */
  let currentContextLabel = null;
  function readLastActivity() {
    return parseActivityStamp(readCookieValue(cookies.readAll(), ACTIVITY_COOKIE));
  }
  function rawSessionEnd() {
    return readCookieValue(cookies.readAll(), END_COOKIE);
  }

  /**
   * The session-end signal, if it is one this tab should obey.
   *
   * ── Why a tab ignores an end cookie that was already there ──
   *
   * This cookie is a live broadcast to tabs that are already open: "the
   * session just ended, lock yourselves now". It is not a record of state.
   *
   * A tab that is only now loading missed the broadcast, and must not act on
   * it. It has a better source: it asks the server. If the session really did
   * end, the token was revoked, /me answers 401, and the app shows its
   * sign-in screen. If the server says the session is live, then it is live,
   * whatever an old cookie says.
   *
   * Getting this wrong is not theoretical. It shipped: somebody was signed
   * out, signed back in, opened Channels, and Channels read the leftover
   * cookie and locked itself against a session that had just been proved
   * good. The cookie had not been cleared because the clearing was hung on a
   * Hanko SDK event that the sign-in screens do not raise.
   *
   * So this tab remembers what was in the cookie when it started, and only
   * obeys a value that is different. A real sign-out writes a new timestamp,
   * so it always differs.
   */
  function readSessionEnd() {
    const raw = rawSessionEnd();
    if (!raw) return null;
    if (ignoredEndRaw !== null && raw === ignoredEndRaw) return null;
    return parseSessionEnd(raw);
  }

  /** The phase right now, computed fresh from the cookies. */
  function readState() {
    return computePhase({
      now: now(),
      lastActivityAt: readLastActivity(),
      sessionEnd: readSessionEnd(),
      warnAfterMs,
      lockAfterMs
    });
  }

  /**
   * Record that a person did something. Throttled, because otherwise every
   * keystroke writes a cookie.
   *
   * Once the session has ended, activity means nothing. A locked tab must not
   * be able to push the clock forward and unlock itself.
   */
  function noteActivity({
    force = false
  } = {}) {
    if (readSessionEnd()) return;
    const at = now();
    if (!force && at - lastWriteAt < writeThrottleMs) return;
    lastWriteAt = at;
    cookies.write(ACTIVITY_COOKIE, String(at));
    draftsSavedThisStretch = false;
    sendHeartbeat(at);
  }

  /**
   * Tell the server a person is here, at most once every heartbeatMs.
   *
   * Separate from the cookie throttle because the two answer different
   * questions. The cookie is free and local; this is a network call.
   *
   * It must never throw into the caller. A heartbeat that fails is a backstop
   * that missed, not a reason to stop recording activity locally — and the
   * browser timer, which is the primary control, is unaffected.
   */
  function sendHeartbeat(at) {
    if (typeof onHeartbeat !== 'function') return;
    if (at - lastHeartbeatAt < heartbeatMs) return;
    lastHeartbeatAt = at;
    try {
      const result = onHeartbeat();
      if (result && typeof result.catch === 'function') result.catch(() => undefined);
    } catch (err) {
      // As above: a failed heartbeat changes nothing the person can see.
    }
  }

  /**
   * The "Stay signed in" button. Writes immediately, ignoring the throttle,
   * so the warning clears in every tab on their next read.
   */
  function staySignedIn() {
    noteActivity({
      force: true
    });
    evaluate();
  }

  /**
   * Name what the person has open right now, so the lock screen can say what
   * it was instead of just that it happened.
   *
   * Call this whenever the thing being worked on changes — a survey loads, a
   * different channel panel opens. An empty or non-string value clears it,
   * which is the right call when nothing named is open.
   */
  function setContextLabel(label) {
    currentContextLabel = typeof label === 'string' && label.trim() ? label.trim() : null;
    notifyCurrent();
  }

  /**
   * Register something that holds unsaved work.
   *
   * The function is called when the warning fires, two minutes before the
   * lock. It should write a local draft and return, or return a promise that
   * settles when it has. It must not throw; a saver that fails must not stop
   * the others from running.
   *
   * What it returns is reported back through `lastSaveResults`, so the lock
   * screen can say what actually happened instead of guessing. Return
   * `{ saved: true, where: 'local', label: 'Customer survey' }`, or
   * `{ saved: false, error: '…' }`, or nothing at all when there was no
   * unsaved work to rescue.
   *
   * @returns {function} call it to unregister.
   */
  function registerDraftSaver(saver) {
    if (typeof saver !== 'function') return () => {};
    const id = nextSaverId++;
    draftSavers.set(id, saver);
    return () => {
      draftSavers.delete(id);
    };
  }

  /**
   * Run every registered saver, once per idle stretch.
   *
   * Idempotent on purpose. A tab that was asleep can jump straight from
   * active to locked without ever passing through the warning, so the lock
   * calls this too. Calling it twice must not save twice.
   *
   * @returns {Promise<Array>} the savers' results. Never rejects.
   */
  function ensureDraftsSaved() {
    if (savePromise) return savePromise;
    if (draftsSavedThisStretch) return Promise.resolve(lastSaveResults);
    draftsSavedThisStretch = true;
    const running = [...draftSavers.values()].map(saver => {
      try {
        return Promise.resolve(saver());
      } catch (err) {
        // A saver that throws has lost its own work. It must not take
        // the other savers, or the sign-out, down with it. Record the
        // failure so the screen can be honest about it.
        return Promise.resolve({
          saved: false,
          error: String(err && err.message || err)
        });
      }
    });
    savePromise = Promise.all(running.map(p => p.catch(err => ({
      saved: false,
      error: String(err && err.message || err)
    })))).then(results => {
      lastSaveResults = results.filter(r => r && typeof r === 'object');
      savePromise = null;
      notifyCurrent();
      return lastSaveResults;
    });
    return savePromise;
  }

  /** What the last run of the savers reported. Empty until they have run. */
  function getLastSaveResults() {
    return lastSaveResults;
  }

  /** True while a draft save is still running. */
  function isSaving() {
    return savePromise !== null;
  }

  /**
   * End the session, once, and say why.
   *
   * Returns true if this call is the one that ended it, and false if it was
   * already over. Only the tab that gets true should talk to the server.
   *
   * This is a check-then-write, not a real lock, so two tabs crossing the
   * threshold in the same millisecond can both get true. That is tolerated: a
   * second sign-out call just fails against an already-revoked token, and
   * building an election protocol for a harmless duplicate is not worth it.
   */
  function endSession(reason = END_REASON_IDLE) {
    if (readSessionEnd()) return false;
    cookies.write(END_COOKIE, `${now()}:${reason}`);
    claimedSignOut = true;
    return true;
  }

  /**
   * Start a fresh session. Called when somebody signs in.
   *
   * Without this a person who signs back in is locked out again on the next
   * tick, by the end cookie left over from the sign-out before.
   */
  function startSession() {
    cookies.remove(END_COOKIE);
    // The cookie is gone, so there is nothing left to ignore. Without this
    // a later real sign-out could write the same value again and be
    // mistaken for the old one.
    ignoredEndRaw = null;
    claimedSignOut = false;
    draftsSavedThisStretch = false;
    lastSaveResults = [];
    currentContextLabel = null;
    lastPhase = PHASE_ACTIVE;
    lastWriteAt = 0;
    // Signing in is a human act, so the server hears about it straight
    // away rather than up to a minute later.
    lastHeartbeatAt = -Infinity;
    noteActivity({
      force: true
    });
    evaluate();
  }
  function notify(state) {
    for (const fn of subscribers) {
      try {
        fn(state);
      } catch (err) {
        // One bad subscriber must not stop the others being told.
      }
    }
  }

  /**
   * Tell everybody where we are without re-running the phase-entry work.
   *
   * Used when something other than time changed — a draft save finishing, for
   * instance. Going through evaluate() would risk starting the savers again.
   */
  function notifyCurrent() {
    const state = readState();
    notify({
      ...state,
      saving: isSaving(),
      saveResults: lastSaveResults,
      contextLabel: currentContextLabel,
      shouldSignOut: state.phase === PHASE_LOCKED && claimedSignOut
    });
  }

  /**
   * Look at the clock and tell everybody where we are.
   *
   * This is the only place phases change, and it runs on the tick, on focus,
   * and whenever the tab becomes visible.
   */
  function evaluate() {
    const state = readState();
    const entering = state.phase !== lastPhase;
    lastPhase = state.phase;
    if (state.phase === PHASE_WARNING && entering) {
      // Save now, two minutes before the lock, not at the lock. That gap
      // is the point: it gives a slow save time to finish, and it covers
      // a browser crash in the last two minutes too.
      ensureDraftsSaved();
    }
    if (state.phase === PHASE_LOCKED && entering) {
      // A tab that was asleep can arrive here without ever showing a
      // warning, so the drafts may not be saved yet.
      ensureDraftsSaved();
    }
    notify({
      ...state,
      saving: isSaving(),
      saveResults: lastSaveResults,
      contextLabel: currentContextLabel,
      // Only the tab that claimed the sign-out should perform it.
      shouldSignOut: state.phase === PHASE_LOCKED && claimedSignOut
    });
  }

  /**
   * Cross the line into a locked session, and report whether this tab owns
   * the sign-out.
   *
   * The caller must wait for the returned promise before revoking the token.
   * Revoking it while a save is in flight makes that save fail with a 401,
   * which is the exact thing this feature exists to prevent.
   *
   * @returns {Promise<{owned: boolean}>}
   */
  function lockNow(reason = END_REASON_IDLE) {
    const owned = endSession(reason);
    const saved = ensureDraftsSaved();
    // Wait for the drafts, but not for ever. The local draft is already
    // written by this point, so a save that hangs costs nothing but time.
    const graced = Promise.race([saved, new Promise(resolve => setTimeout(resolve, saveGraceMs))]);
    return graced.then(() => {
      evaluate();
      return {
        owned
      };
    });
  }
  const onActivity = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    noteActivity();
  };
  const onVisibility = () => {
    if (typeof document === 'undefined') return;
    if (document.visibilityState === 'visible') {
      // Coming back to a tab is itself activity, but only check the clock
      // first — a tab that was hidden for an hour must lock, not reset.
      evaluate();
      if (lastPhase === PHASE_ACTIVE) noteActivity();
    }
  };
  function subscribe(fn) {
    if (typeof fn !== 'function') return () => {};
    subscribers.add(fn);
    fn({
      ...readState(),
      saving: isSaving(),
      saveResults: lastSaveResults,
      contextLabel: currentContextLabel,
      shouldSignOut: false
    });
    return () => {
      subscribers.delete(fn);
    };
  }
  function start() {
    if (running) return;
    running = true;
    if (typeof window !== 'undefined') {
      for (const name of ACTIVITY_EVENTS) {
        window.addEventListener(name, onActivity, {
          capture: true,
          passive: true
        });
      }
      window.addEventListener('focus', onVisibility);
    }
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility);
    }
    // Remember the broadcast this tab was not open for, so it does not act
    // on it. See readSessionEnd for why that matters.
    ignoredEndRaw = rawSessionEnd() || null;
    // Seed the cookie so a first-ever tab is not treated as idle.
    if (readLastActivity() === null && !readSessionEnd()) noteActivity({
      force: true
    });
    timer = setInterval(evaluate, tickMs);
    evaluate();
  }
  function stop() {
    if (!running) return;
    running = false;
    if (timer) clearInterval(timer);
    timer = null;
    if (typeof window !== 'undefined') {
      for (const name of ACTIVITY_EVENTS) {
        window.removeEventListener(name, onActivity, {
          capture: true
        });
      }
      window.removeEventListener('focus', onVisibility);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility);
    }
  }
  return {
    start,
    stop,
    subscribe,
    readState,
    evaluate,
    noteActivity,
    staySignedIn,
    registerDraftSaver,
    ensureDraftsSaved,
    getLastSaveResults,
    setContextLabel,
    getContextLabel: () => currentContextLabel,
    isSaving,
    endSession,
    startSession,
    lockNow,
    // Exposed for tests and for apps that need to name the cookies.
    cookieNames: {
      activity: ACTIVITY_COOKIE,
      end: END_COOKIE
    }
  };
}
var _default = exports.default = createIdleClock;